import { useContext } from 'react';
import { WysiwygEditorStateContext } from '@src/contexts/wysiwyg/editor';
import _clamp from 'lodash/clamp';
import type { TImageContentItem } from '@src/types/wysiwyg_editor'; import useConstrainedPageItemLocation from './useConstrainedPageItemLocation';
import useConstrainedPageItemDimensions from './useConstrainedPageItemDimensions';

interface Config {
  url: string;
  imageDimensions: { height: number; width: number };
  hopefulLocation?: {
    x: number;
    y: number;
  };
  dragOffset?: { x: number; y: number };
  centered?: boolean;
}

type Generator = (config: Config) => TImageContentItem;

type NoOp = () => null;

const contentTypeId = 'image';
let generationId = 1;

export function useImageGenerator(): Generator | NoOp {
  const { offsetParentRef, snapEdges } = useContext(WysiwygEditorStateContext);
  const constrainedPageItemLocation = useConstrainedPageItemLocation();
  const constrainedPageItemDimensions = useConstrainedPageItemDimensions();

  if (!offsetParentRef.current) return () => null;

  return function imageGenerator({
    url,
    imageDimensions,
    hopefulLocation = { x: 0, y: 0 },
    dragOffset = { x: 0, y: 0 },
    centered = false
  }: Config): TImageContentItem {
    const id = `${contentTypeId}-g-${generationId++}-${Date.now()}`;

    const { height, width } = constrainedPageItemDimensions({
      hopefulDimensions: imageDimensions,
      returnMeasurementType: '%'
    });

    let x: number;
    let y: number;

    if (centered) {
      const imageMidX = width / 2;
      const imageMidY = height / 2;

      x = snapEdges.left + (snapEdges.right - snapEdges.left) / 2 - imageMidX;
      y = snapEdges.top + (snapEdges.bottom - snapEdges.top) / 2 - imageMidY;
    } else {
      const location = constrainedPageItemLocation({
        dimensions: { height, width },
        hopefulLocation,
        origin: dragOffset,
        dimensionsMeasurementType: '%',
        returnMeasurementType: '%'
      });

      x = location.x;
      y = location.y;
    }

    const location = { height, width, x, y };
    const zoom = 1;

    return {
      id,
      containerLocation: location,
      contentLocation: { height, width, x: 0, y: 0 },
      href: url,
      type: contentTypeId,
      zoom: zoom
    };
  };
}
